import type { TiptapDoc } from './types';

interface TiptapNode {
    type?: string;
    attrs?: Record<string, unknown>;
    content?: TiptapNode[];
}

function walkNodes(nodes: TiptapNode[] | undefined, visitor: (n: TiptapNode) => void): void {
    if (!nodes) return;
    for (const node of nodes) {
        visitor(node);
        if (Array.isArray(node.content)) walkNodes(node.content, visitor);
    }
}

/** Extracts the file-id from a node that references an uploaded image, or null.
 *  - Plain `image` nodes store the id in `attrs['data-file-id']`.
 *  - `imageGridCell` nodes (ImageGrid extension) store it in `attrs.fileId`. */
function fileIdFromNode(node: TiptapNode): string | null {
    if (node.type === 'image') {
        const id = node.attrs?.['data-file-id'];
        return typeof id === 'string' && id.length > 0 ? id : null;
    }
    if (node.type === 'imageGridCell') {
        const id = node.attrs?.fileId;
        return typeof id === 'string' && id.length > 0 ? id : null;
    }
    return null;
}

/** Collects every distinct file-id from image nodes in a single TiptapDoc.
 *  Covers both plain `image` nodes (`data-file-id`) and `imageGridCell` nodes (`fileId`). */
export function collectDocImageFileIds(doc: TiptapDoc): string[] {
    const ids = new Set<string>();
    walkNodes(doc.content as TiptapNode[], (node) => {
        const id = fileIdFromNode(node);
        if (id) ids.add(id);
    });
    return [...ids];
}

/** Maps each image node's file-id to its current `src`. Same node coverage as
 *  collectDocImageFileIds; nodes without both an id and a src are skipped. */
export function collectDocImageSrcs(doc: TiptapDoc): Record<string, string> {
    const srcs: Record<string, string> = {};
    walkNodes(doc.content as TiptapNode[], (node) => {
        const id = fileIdFromNode(node);
        const src = node.attrs?.src;
        if (id && typeof src === 'string' && src.length > 0) srcs[id] = src;
    });
    return srcs;
}

function rewriteNode(node: TiptapNode, urls: Record<string, string>): TiptapNode {
    const id = fileIdFromNode(node);
    if (id && urls[id]) {
        return { ...node, attrs: { ...node.attrs, src: urls[id] } };
    }
    if (Array.isArray(node.content)) {
        return { ...node, content: node.content.map((c) => rewriteNode(c, urls)) };
    }
    return node;
}

/** Returns a new TiptapDoc with every image node's `src` swapped for the resolved URL.
 *  Covers plain `image` nodes and `imageGridCell` nodes. Immutable. */
export function applyResolvedUrlsToDoc(doc: TiptapDoc, urls: Record<string, string>): TiptapDoc {
    return {
        ...doc,
        content: ((doc.content as TiptapNode[]) ?? []).map((n) => rewriteNode(n, urls))
    };
}
